import "./App.css";
import "./styles/layout.css";
import "./styles/responsive.css";

import { Navigate, Route, Routes } from "react-router-dom";

import {
  PATIENT_BASE_SEGMENT,
  PATIENT_CHILD_SEGMENTS,
  ROUTE_SEGMENTS,
  ROUTES,
} from "utils/routes";
import { restoreSession } from "utils/sessionTokens";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthenticatedLayout } from "./layouts/AuthenticatedLayout";
import { GlobalLayout } from "./layouts/GlobalLayout";
import { LegacyPatientRedirect } from "./layouts/LegacyPatientRedirect";
import { PatientLayout } from "./layouts/PatientLayout";
import { PrivateRouteLayout } from "./layouts/PrivateRouteLayout";
import { SettingsLayout } from "./layouts/SettingsLayout/SettingsLayout";
import { ForgotPasswordPage as ForgotPassword } from "./pages/auth/ForgotPasswordPage";
import { LoginPage as LogIn } from "./pages/auth/LoginPage";
import { LogoutPage as Logout } from "./pages/auth/LogoutPage";
import { ParticipantVideoJoinPage as ParticipantVideoJoin } from "./pages/auth/ParticipantVideoJoinPage";
import { RegistrationPage as Registration } from "./pages/auth/RegistrationPage";
import { ResetPasswordPage as ResetPasswordEmail } from "./pages/auth/ResetPasswordPage";
import { SignUpFivePage as SignUpStepFive } from "./pages/auth/SignUpFivePage";
import { SignUpFourPage as SignUpStepFour } from "./pages/auth/SignUpFourPage";
import { SignUpPage as SignUp } from "./pages/auth/SignUpPage";
import { SignUpSixPage as SignUpSix } from "./pages/auth/SignUpSixPage";
import { SignUpThreePage as SignUpStepThree } from "./pages/auth/SignUpThreePage";
import { SignUpTwoPage as SignUpStepTwo } from "./pages/auth/SignUpTwoPage";
import { SuccessMsgPage as SuccessMsg } from "./pages/auth/SuccessMsgPage";
import { VideoCallPage as VideoCall } from "./pages/auth/VideoCallPage";
import { AppointmentsPage as Appointments } from "./pages/global/AppointmentsPage";
import { CommunicationsPage as Communications } from "./pages/global/CommunicationsPage";
import { DocumentsPage as GlobalDocuments } from "./pages/global/DocumentsPage";
import { HomePage as Home } from "./pages/global/HomePage";
import { ImmunizationsPage as Immunizations } from "./pages/global/ImmunizationsPage";
import { NotFoundPage } from "./pages/global/NotFoundPage/NotFoundPage";
import { PatientMainPage as PatientMain } from "./pages/global/PatientMainPage";
import { PatientPage as Patient } from "./pages/global/PatientPage";
import { PaymentsPage as Payments } from "./pages/global/PaymentsPage";
import { ReportsPage as Reports } from "./pages/global/ReportsPage";
import { ResultsPage as Results } from "./pages/global/ResultsPage";
import { ConsultationEditPage } from "./pages/patient/ConsultationEditPage";
import { PatientBaselinePage as PatientBaseline } from "./pages/patient/PatientBaselinePage";
import { PatientCDMPage as PatientCDM } from "./pages/patient/PatientCDMPage";
import { PatientCommunicationPage as PatientCommunication } from "./pages/patient/PatientCommunicationPage";
import { PatientConsultationsPage as PatientConsultations } from "./pages/patient/PatientConsultationsPage";
import { PatientConsultPage as PatientConsult } from "./pages/patient/PatientConsultPage";
import { PatientDetailsPage } from "./pages/patient/PatientDetailsPage";
import { PatientDocumentsPage as PatientDocumentsGp } from "./pages/patient/PatientDocumentsPage";
import { PatientFamilyPage as PatientFamily } from "./pages/patient/PatientFamilyPage";
import { PatientImmunizationsPage as PatientImmunizations } from "./pages/patient/PatientImmunizationsPage";
import { PatientLabOrdersPage as PatientLabOrders } from "./pages/patient/PatientLabOrdersPage";
import { PatientMedicationPage as PatientMedication } from "./pages/patient/PatientMedicationPage";
import { PatientPaymentsPage as PatientPayments } from "./pages/patient/PatientPaymentsPage";
import { PatientPregnancyPage as PatientPregnancy } from "./pages/patient/PatientPregnancyPage";
import { PatientReferralsPage as PatientReferrals } from "./pages/patient/PatientReferralsPage";
import { PatientResultsPage as PatientResult } from "./pages/patient/PatientResultsPage";
import { PatientTasksPage as PatientTasks } from "./pages/patient/PatientTasksPage";
import { ConsultationNoteTemplatesPage as ConsultationNoteTemplates } from "./pages/settings/ConsultationNoteTemplatesPage";
import { DocumentTypesPage as DocumentTypes } from "./pages/settings/DocumentTypesPage";
import { DrugsFilePage as DrugsFile } from "./pages/settings/DrugsFilePage";
import { EmailTemplatesPage as EmailTemplates } from "./pages/settings/EmailTemplatesPage";
import { HeaderFooterPage as HeaderFooter } from "./pages/settings/HeaderFooterPage";
import { HealthlinkDetailsPage as HealthlinkDetails } from "./pages/settings/HealthlinkDetailsPage";
import { LetterTemplatesPage as LetterTemplates } from "./pages/settings/LetterTemplatesPage";
import { LocationsPage as Locations } from "./pages/settings/LocationsPage";
import { MergePatientsPage } from "./pages/settings/MergePatientsPage";
import { MobileAppPage as MobileApp } from "./pages/settings/MobileAppPage";
import { PaymentDetailsPage as PaymentDetails } from "./pages/settings/PaymentDetailsPage";
import { PracticeDetailsPage as PracticeDetails } from "./pages/settings/PracticeDetailsPage";
import { PracticeHoursDetailsPage as PracticeHoursDetails } from "./pages/settings/PracticeHoursDetailsPage";
import { PracticeSettingsPage as PracticeSettings } from "./pages/settings/PracticeSettingsPage";
import { Profile } from "./pages/settings/ProfilePage";
import { ServiceDetailsPage as ServiceDetails } from "./pages/settings/ServiceDetailsPage";
import { SmsSettingsPage as SmsSettings } from "./pages/settings/SmsSettingsPage";
import { UserDetailsPage as UserDetails } from "./pages/settings/UserDetailsPage";
import { VaccinesPage as Vaccines } from "./pages/settings/VaccinesPage";

function App() {
  // A new tab starts signed in by copying the tokens back out of localStorage. It used to copy
  // them unconditionally, writing `null` into the session once a logout had removed them —
  // see restoreSession.
  restoreSession();

  return (
    <GlobalLayout>
      <Routes>
        <Route path={ROUTES.AUTH.HOME} element={<LogIn />} />
        <Route path={ROUTES.AUTH.LOGIN} element={<LogIn />} />
        <Route path={ROUTES.AUTH.LOGOUT} element={<Logout />} />
        <Route path={ROUTES.AUTH.REGISTRATION} element={<Registration />} />
        <Route
          path={ROUTES.AUTH.FORGOT_PASSWORD}
          element={<ForgotPassword />}
        />
        <Route
          path={ROUTES.AUTH.RESET_PASSWORD}
          element={<ResetPasswordEmail />}
        />
        <Route path={ROUTES.AUTH.SUCCESS_MSG} element={<SuccessMsg />} />
        <Route path={ROUTES.AUTH.SIGNUP} element={<SignUp />} />
        <Route path={ROUTES.AUTH.SIGNUP_TWO} element={<SignUpStepTwo />} />
        <Route path={ROUTES.AUTH.SIGNUP_THREE} element={<SignUpStepThree />} />
        <Route path={ROUTES.AUTH.SIGNUP_FOUR} element={<SignUpStepFour />} />
        <Route path={ROUTES.AUTH.SIGNUP_FIVE} element={<SignUpStepFive />} />
        <Route path={ROUTES.AUTH.SIGNUP_SIX} element={<SignUpSix />} />
        <Route path={ROUTES.AUTH.VIDEO_CALL} element={<VideoCall />} />
        <Route
          path={ROUTES.AUTH.PARTICIPANT_VIDEO_JOIN}
          element={<ParticipantVideoJoin />}
        />

        <Route path="/allied_home" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<PrivateRouteLayout />}>
          <Route element={<AuthenticatedLayout />}>
            <Route
              element={
                <ProtectedRoute
                  roles={["ADMIN", "ROLE_ADMIN", "ROLE_REPORTS"]}
                />
              }
            >
              <Route
                path={ROUTE_SEGMENTS.GLOBAL.REPORTS}
                element={<Reports />}
              />
              <Route
                path={ROUTE_SEGMENTS.GLOBAL.REPORTS_TYPE}
                element={<Reports />}
              />
              <Route
                path={ROUTE_SEGMENTS.GLOBAL.CDM_FINDERS}
                element={<Reports />}
              />
              <Route
                path={ROUTE_SEGMENTS.GLOBAL.CDM_FINDERS_TYPE}
                element={<Reports />}
              />
            </Route>
            <Route path={ROUTE_SEGMENTS.GLOBAL.RESULTS} element={<Results />} />
            <Route
              path={ROUTE_SEGMENTS.GLOBAL.IMMUNIZATIONS}
              element={<Immunizations />}
            />

            <Route path={ROUTE_SEGMENTS.GLOBAL.PATIENT} element={<Patient />} />
            <Route path={ROUTE_SEGMENTS.GLOBAL.HOME} element={<Home />} />
            <Route
              path={ROUTE_SEGMENTS.GLOBAL.PATIENT}
              element={<PatientMain />}
            />
            <Route
              path={ROUTE_SEGMENTS.GLOBAL.DOCUMENTS}
              element={<GlobalDocuments />}
            />
            <Route element={<SettingsLayout />}>
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PRACTICE_DETAILS}
                element={<PracticeDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.LOCATIONS}
                element={<Locations />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.USER_DETAILS}
                element={<UserDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.SERVICE_DETAILS}
                element={<ServiceDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.DRUGS_FILE}
                element={<DrugsFile />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.DOCUMENT_TYPES}
                element={<DocumentTypes />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.VACCINES}
                element={<Vaccines />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PRACTICE_HOURS}
                element={<PracticeHoursDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.SMS_SETTINGS}
                element={<SmsSettings />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.CONSULTATION_TEMPLATES}
                element={<ConsultationNoteTemplates />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.LETTER_TEMPLATES}
                element={<LetterTemplates />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PRACTICE_SETTINGS}
                element={<PracticeSettings />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.EMAIL_TEMPLATES}
                element={<EmailTemplates />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PAYMENTS_DETAILS}
                element={<PaymentDetails />}
              />
              {/* Stripe-hosted onboarding return/refresh targets — same
                  component as PAYMENTS_DETAILS, it detects the suffix itself. */}
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PAYMENTS_DETAILS_ONBOARDING_COMPLETE}
                element={<PaymentDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.PAYMENTS_DETAILS_ONBOARDING_REFRESH}
                element={<PaymentDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.HEADERS_FOOTERS}
                element={<HeaderFooter />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.HEALTHLINK}
                element={<HealthlinkDetails />}
              />
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.MOBILE_APP}
                element={<MobileApp />}
              />
            </Route>
            <Route
              element={
                <ProtectedRoute
                  roles={["ADMIN", "ROLE_ADMIN", "ROLE_PAYMENTS"]}
                />
              }
            >
              <Route
                path={ROUTE_SEGMENTS.GLOBAL.PAYMENTS}
                element={<Payments />}
              />
            </Route>
            {/* Merging moves clinical data between two patient records, so it stays
                ADMIN-only. No longer listed in the Settings nav (merge history is
                now visible as a report instead) nor nested under SettingsLayout, so
                it needs its own explicit role gate rather than inheriting one. */}
            <Route element={<ProtectedRoute roles={["ADMIN", "ROLE_ADMIN"]} />}>
              <Route
                path={ROUTE_SEGMENTS.SETTINGS.MERGE_PATIENTS}
                element={<MergePatientsPage />}
              />
            </Route>
            <Route
              path={ROUTE_SEGMENTS.SETTINGS.PROFILE}
              element={<Profile />}
            />
            <Route
              path={ROUTE_SEGMENTS.GLOBAL.APPOINTMENTS}
              element={<Appointments />}
            />
            <Route
              path={ROUTE_SEGMENTS.GLOBAL.COMMUNICATIONS}
              element={<Communications />}
            />
            {/* Consultation editor: /patients/:patientId/consultation/edit/:consultationId
                (standalone — no NavigationBar). */}
            <Route
              path={ROUTE_SEGMENTS.CONSULTATION_EDIT}
              element={<ConsultationEditPage />}
            />

            {/* Patient pages: id in the path, NavigationBar via PatientLayout. */}
            <Route path={PATIENT_BASE_SEGMENT} element={<PatientLayout />}>
              {/*
                A patient link that names no tab — a bookmark, a refresh, a link pasted
                between staff — used to match this layout with nothing under it, so the
                header and tabs rendered above an empty area and not one per-patient
                request was made. It read as a patient with no record rather than as a
                broken link (KIN-2118). Relative, so the id stays the parent's.
              */}
              <Route
                index
                element={
                  <Navigate to={PATIENT_CHILD_SEGMENTS.DETAILS} replace />
                }
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.DETAILS}
                element={<PatientDetailsPage />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.PREGNANCY}
                element={<PatientPregnancy />}
              />
              <Route path={PATIENT_CHILD_SEGMENTS.CDM} element={<PatientCDM />} />
              <Route
                path={PATIENT_CHILD_SEGMENTS.REFERRALS}
                element={<PatientReferrals />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.LAB_ORDERS}
                element={<PatientLabOrders />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.DOCUMENTS}
                element={<PatientDocumentsGp />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.COMMUNICATION}
                element={<PatientCommunication />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.PAYMENTS}
                element={<PatientPayments />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.FAMILY}
                element={<PatientFamily />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.CONSULT}
                element={<PatientConsult />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.CONSULTATIONS}
                element={<PatientConsultations />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.RESULTS}
                element={<PatientResult />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.IMMUNIZATIONS}
                element={<PatientImmunizations />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.MEDICATION}
                element={<PatientMedication />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.BASELINE}
                element={<PatientBaseline />}
              />
              <Route
                path={PATIENT_CHILD_SEGMENTS.TASKS}
                element={<PatientTasks />}
              />
            </Route>

            {/* Legacy id-in-query redirects: keep already-sent links working. */}
            <Route
              path="patient/consultation/edit/:consultationId"
              element={<LegacyPatientRedirect />}
            />
            <Route
              path="patient/:legacyTab"
              element={<LegacyPatientRedirect />}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </GlobalLayout>
  );
}

export { App };
